import {
	FormItem,
	InputWithMoreInfo,
	Select,
	TextInput,
	ValidationError,
	AlertDialog,
	Checkbox,
	SelectOption
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { createValidationConfiguration, InputValidationResultKind } from "client/Validation";
import {
	inline,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { AppType } from "client/Api/Apps/common";
import {
	conditionalAccessValidator,
	initialConditionalAccessState,
	prepareConditionalAccessForSaving
} from "../ConditionalAccess";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import {
	initialMaintenanceWindowState,
	maintenanceWindowValidator,
	prepareMaintenanceWindowForSaving
} from "../MaintenanceWindow";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import { AlertManager, useAlerts } from "client/Hooks";
import {
	EditWindowsVirtualDesktopAppViewModel,
	SaveWindowsVirtualDesktopAppModel,
	RemoteDesktopProtocolClientKind,
	WindowsVirtualDesktopVersionKind,
	saveWindowsVirtualDesktopApp
} from "client/Api/Apps/EditApp/saveApp";
import { IValueInputAttachable } from "client/Forms/attachable";

export type EditWindowsVirtualDesktopAppProps = {
	initialSettings: EditAppSettings<EditWindowsVirtualDesktopAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditWindowsVirtualDesktopApp = forwardRef(
	(
		props: EditWindowsVirtualDesktopAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [shouldShowAppNameInfo, setShouldShowAppNameInfo] = useState<boolean>(false);
		const [formData, setFormData] = useState<SaveWindowsVirtualDesktopAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveWindowsVirtualDesktopAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const prepareModelForSaving = useCallback(
			(model: SaveWindowsVirtualDesktopAppModel): SaveWindowsVirtualDesktopAppModel => {
				return {
					...model,
					maintenanceSettings: prepareMaintenanceWindowForSaving(model.maintenanceSettings),
					conditionalAccessSettings: prepareConditionalAccessForSaving(model.conditionalAccessSettings)
				};
			},
			[]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveWindowsVirtualDesktopAppModel>(
			formHandler,
			saveWindowsVirtualDesktopApp,
			AppType.WindowsVirtualDesktop,
			prepareModelForSaving
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		useEffect(
			(): void => {
				alertManager
					.showInfo(localize("_MakeSureThatOAuth2IsConfiguredAsSingleSignOnForThisApp"));
			},
			[alertManager]
		);

		const clientOptions = useMemo(
			(): readonly SelectOption<RemoteDesktopProtocolClientKind>[] => {
				if (formData.versionKind === WindowsVirtualDesktopVersionKind.Arm) {
					return [
						...windows365OnlyClientKindOptions,
						...clientKindOptions
					];
				}

				return clientKindOptions;
			},
			[formData.versionKind]
		);

		useEffect(
			(): void => {
				if (formData.versionKind === WindowsVirtualDesktopVersionKind.Arm) {
					return;
				}

				if (formData.client
					&& windows365OnlyClientKindOptions.some((x) => x.value === formData.client)
				) {
					const clientAttachable: IValueInputAttachable<RemoteDesktopProtocolClientKind | null> = formHandler
						.for("client");

					clientAttachable.reducer
						.setValue(null);

					clientAttachable.validation
						.triggerInputFinished();
				}
			},
			[
				formData.client,
				formData.versionKind,
				formHandler
			]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under(
				"versionKind",
				"appName",
				"client",
				"useMicrosoftTerminalServicesClientOnMacOs"
			)
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem
					label={localize("_AzureVirtualDesktopVersion")}
					isRequired
				>
					<Select
						{...formHandler.for("versionKind")
							.attachToSelect()
						}
						options={versionOptions}
						menuTitle={localize("_AzureVirtualDesktopVersion")}
					/>
					<ValidationError
						{...formHandler
							.for("versionKind")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem
					label={localize("_AzureVirtualDesktopAppName")}
					isRequired
				>
					<InputWithMoreInfo
						onMoreInfo={() => setShouldShowAppNameInfo(true)}
					>
						<TextInput
							{...formHandler.for("appName")
								.attachToTextInput()
							}
						/>
						<ValidationError
							{...formHandler
								.for("appName")
								.attachToValidationError()
							}
						/>
					</InputWithMoreInfo>
				</FormItem>
				{shouldShowAppNameInfo && (
					<AppNameInfoDialog
						onClose={() => setShouldShowAppNameInfo(false)}
					/>
				)}
				<FormItem
					label={localize("_RemoteDesktopClient")}
					isRequired
				>
					<Select
						{...formHandler.for("client")
							.attachToSelect()
						}
						options={clientOptions}
						menuTitle={localize("_RemoteDesktopClient")}
					/>
					<ValidationError
						{...formHandler
							.for("client")
							.attachToValidationError()
						}
					/>
				</FormItem>
				{formData.client === RemoteDesktopProtocolClientKind.MicrosoftRemoteDesktopClient && (
					<FormItem
						label={localize("_UseMicrosoftTerminalServicesClientOnMacOs")}
					>
						<Checkbox
							{...formHandler.for("useMicrosoftTerminalServicesClientOnMacOs")
								.attachToCheckbox()
							}
						/>
						<ValidationError
							{...formHandler
								.for("useMicrosoftTerminalServicesClientOnMacOs")
								.attachToValidationError()
							}
						/>
					</FormItem>
				)}
			</EditAppBase>
		);
	}
);

EditWindowsVirtualDesktopApp.displayName = "EditWindowsVirtualDesktopApp";

const validation = createValidationConfiguration<SaveWindowsVirtualDesktopAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forObject("conditionalAccessSettings")
	.addValidators(conditionalAccessValidator());

validation.forObject("maintenanceSettings")
	.addValidators(maintenanceWindowValidator());

validation.forScalar("versionKind")
	.add(
		isNotNullNorUndefined()
	);

validation.forScalar("appName")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("client")
	.add(
		isNotNullNorUndefined()
	);

validation.when((x) => x.versionKind === WindowsVirtualDesktopVersionKind.NoneArm)
	.forScalar("client")
	.add(
		inline(
			(x) => {
				if (x === RemoteDesktopProtocolClientKind.WindowsApp
					|| x === RemoteDesktopProtocolClientKind.Web
				) {
					return {
						kind: InputValidationResultKind.Invalid,
						errorMessage: localize("_InvalidInput")
					};
				}

				return { kind: InputValidationResultKind.Valid };
			}
		)
	);

const versionOptions: readonly SelectOption<WindowsVirtualDesktopVersionKind>[] = [
	{ text: localize("_AzureVirtualDesktop"), value: WindowsVirtualDesktopVersionKind.NoneArm },
	{ text: localize("_AzureVirtualDesktopArm"), value: WindowsVirtualDesktopVersionKind.Arm },
];

const clientKindOptions: readonly SelectOption<RemoteDesktopProtocolClientKind>[] = [
	{ text: localize("_Mstsc"), value: RemoteDesktopProtocolClientKind.MicrosoftTerminalServicesClient },
	{ text: localize("_Msrdc"), value: RemoteDesktopProtocolClientKind.MicrosoftRemoteDesktopClient }
];

const windows365OnlyClientKindOptions: readonly SelectOption<RemoteDesktopProtocolClientKind>[] = [
	{ text: localize("_WindowsApp"), value: RemoteDesktopProtocolClientKind.WindowsApp },
	{ text: localize("_Web"), value: RemoteDesktopProtocolClientKind.Web }
];

export type AppNameInfoDialogProps = {
	onClose(): void;
};

export const AppNameInfoDialog = (props: AppNameInfoDialogProps): ReactElement => (
	<AlertDialog
		title={localize("_AzureVirtualDesktopAppName")}
		onClose={props.onClose}
		showCloseButton
		trackingId="app-name-info"
	>
		{localize("_AzureVirtualDesktopAppNameExplanation")}
	</AlertDialog>
);
AppNameInfoDialog.displayName = "AppNameInfoDialog";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditWindowsVirtualDesktopAppViewModel>
): SaveWindowsVirtualDesktopAppModel {
	const {
		maintenanceWindow,
		conditionalAccessSettings,
		versionKind,
		appName,
		client,
		useMicrosoftTerminalServicesClientOnMacOs
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		maintenanceSettings: initialMaintenanceWindowState(maintenanceWindow),
		conditionalAccessSettings: initialConditionalAccessState(conditionalAccessSettings),
		versionKind,
		appName,
		client,
		useMicrosoftTerminalServicesClientOnMacOs
	};
}

